<template>
  <div class="space-y-6">
    <div ref="swipeTarget" class="h-full w-full" style="touch-action: pan-y; user-select: none">
      <div ref="cardRef" class="w-full">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-semibold mr-auto">
            CRM – {{ currentPerson?.firstName }} {{ currentPerson?.lastName }}
          </h2>
          <Button label="Back to List" icon="pi pi-arrow-left" @click="goBack" />
        </div>

        <TabView v-model:activeIndex="activeIndex">
          <TabPanel header="Overview" :value="0">
            <CrmPersonSummary :person-id="currentPerson?.id || ''" />
          </TabPanel>
          <TabPanel header="Transactions" :value="1">
            <TransactionLog :person-id="currentPerson?.id || ''" />
          </TabPanel>
          <TabPanel header="Emails" :value="2">
            <EmailLog :person-id="currentPerson?.id || ''" />
          </TabPanel>
          <TabPanel header="Calls" :value="3">
            <CallTab :person-id="currentPerson?.id || ''" />
          </TabPanel>
        </TabView>
      </div>

      <div class="flex gap-2 mt-4 mb-2">
        <div
          v-for="(_, index) in processedItems"
          :key="index"
          class="w-2 h-2 rounded-full"
          :class="index === currentIndex ? 'bg-primary-500' : 'bg-gray-300'"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePersonStore } from '@/stores/v2/person'
import { useSwipeNavigation } from '@/composables/useSwipeNavigation'
import type { Person } from '@/types/Person'

// Components
import TransactionLog from '@/components/crm/TransactionLog.vue'
import EmailLog from '@/components/crm/EmailsTab.vue'
import CallTab from '@/components/crm/CallsTab.vue'
import Button from 'primevue/button'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import CrmPersonSummary from '@/components/crm/CrmPersonOverview.vue'

const route = useRoute()
const router = useRouter()
const personStore = usePersonStore()
const activeIndex = ref(0)
const loading = ref(true)

// Create refs for the swipe navigation properties
const swipeTarget = ref<HTMLDivElement | null>(null)
const cardRef = ref<HTMLDivElement | null>(null)

// V2 architecture - local state management
const currentPerson = ref<Person | null>(null)
const processedItems = ref<Person[]>([])
const currentIndex = ref(0)

// Initialize swipe navigation when refs are ready
let swipeNavigationInitialized = false
let swipeNavigationInstance: ReturnType<typeof useSwipeNavigation> | null = null

watch(
  [swipeTarget, cardRef],
  ([newSwipeTarget, newCardRef]) => {
    console.log('CrmPersonView: Refs updated:', {
      swipeTarget: newSwipeTarget,
      cardRef: newCardRef,
      swipeTargetElement: newSwipeTarget?.outerHTML,
      cardRefElement: newCardRef?.outerHTML,
      swipeTargetRect: newSwipeTarget?.getBoundingClientRect(),
      cardRefRect: newCardRef?.getBoundingClientRect(),
      alreadyInitialized: swipeNavigationInitialized,
    })

    if (newSwipeTarget && newCardRef && !swipeNavigationInitialized) {
      console.log('CrmPersonView: Initializing swipe navigation')
      swipeNavigationInitialized = true

      swipeNavigationInstance = useSwipeNavigation({
        swipeTarget,
        cardRef,
        onNext: () => {
          console.log('CrmPersonView: Next navigation triggered')
          if (currentIndex.value < processedItems.value.length - 1) {
            currentIndex.value++
            const nextPerson = processedItems.value[currentIndex.value]
            if (nextPerson) {
              currentPerson.value = nextPerson
              router.push(`/crm/people/${nextPerson.id}`)
            }
          }
        },
        onPrevious: () => {
          console.log('CrmPersonView: Previous navigation triggered')
          if (currentIndex.value > 0) {
            currentIndex.value--
            const prevPerson = processedItems.value[currentIndex.value]
            if (prevPerson) {
              currentPerson.value = prevPerson
              router.push(`/crm/people/${prevPerson.id}`)
            }
          }
        },
      })

      // Call setup to initialize event listeners
      swipeNavigationInstance.setup()

      // Clean up on component unmount
      onUnmounted(() => {
        console.log('CrmPersonView: Cleaning up swipe navigation')
        if (swipeNavigationInstance) {
          swipeNavigationInstance.cleanup()
          swipeNavigationInstance = null
        }
        swipeNavigationInitialized = false
      })
    }
  },
  { immediate: true },
)

const goBack = () => router.push('/crm/people/')

// Load people list for navigation
async function loadPeopleList() {
  try {
    const search = await personStore.search()
    processedItems.value = await search.recentlyUpdated(50).run()
  } catch (error) {
    console.error('Error loading people list:', error)
  }
}

// Watch for route changes to update current person
watch(
  () => route.params.id,
  async (newId) => {
    if (newId) {
      const personId = newId as string
      currentPerson.value = await personStore.get(personId)
      // Find the index of current person in the list
      const index = processedItems.value.findIndex((p) => p.id === personId)
      if (index !== -1) {
        currentIndex.value = index
      }
    }
  },
)

// Initialize when component is mounted
onMounted(async () => {
  console.log('CrmPersonView: Component mounted')
  loading.value = true
  try {
    await loadPeopleList()
    // Set the current person based on route
    if (route.params.id) {
      const personId = route.params.id as string
      currentPerson.value = await personStore.get(personId)
      // Find the index of current person in the list
      const index = processedItems.value.findIndex((p) => p.id === personId)
      if (index !== -1) {
        currentIndex.value = index
      }
    }
  } finally {
    loading.value = false
  }
})
</script>

<style>
.p-tabview-panels {
  padding: 0 !important;
}
</style>
